/**
 * Utility functions for detecting and handling table elements in HTML content.
 *
 * Tables are not rendered in the RichTextEditor; instead they are replaced with
 * inline placeholder icons. The original table HTML is stored in a data attribute
 * and restored when content is sent out (e.g. on blur or "Replace to Chart").
 */

import { EDITOR_COLORS } from '../../../constants/statusColors';

/**
 * Returns true if the element is a table, a figure.table wrapper, or a table placeholder.
 * Used when scanning the DOM to identify table-related nodes.
 *
 * @param element - The DOM element to check
 * @returns True for <table>, <figure class="table">, or elements with data-table-placeholder
 */
export function isTableElement(element: HTMLElement | null): boolean {
  if (!element) return false;
  if (!(element instanceof Element)) return false;

  return (
    element.tagName.toLowerCase() === 'table' ||
    (element.tagName.toLowerCase() === 'figure' &&
      element.classList.contains('table')) ||
    element.hasAttribute('data-table-placeholder')
  );
}

/**
 * Returns true if the element is a table placeholder (the inline icon we show instead of a table).
 * Placeholders are <span> elements with data-table-placeholder="true" and store the original
 * table HTML in data-original-html.
 *
 * @param element - The DOM element to check
 */
export function isTablePlaceholder(element: HTMLElement | null): boolean {
  if (!element) return false;
  if (!(element instanceof Element)) return false;
  return element.hasAttribute('data-table-placeholder');
}

/**
 * Finds the closest table placeholder that contains the given node (if any).
 *
 * Used to answer: "Is the cursor/selection currently inside a table placeholder?"
 * When the user clicks or types, we walk up from the target node; if we hit a
 * placeholder before the document root, the cursor is inside a table. We use
 * this to block editing and prevent the caret from entering the placeholder.
 *
 * @param node - A DOM node (e.g. the selection's anchor or the click target)
 * @returns The containing placeholder element, or null if the node is not inside one
 */
export function findTableAncestor(node: Node | null): HTMLElement | null {
  if (!node) return null;

  let current: Node | null = node;
  while (current && current.nodeType !== Node.DOCUMENT_NODE) {
    if (
      current.nodeType === Node.ELEMENT_NODE &&
      isTablePlaceholder(current as HTMLElement)
    ) {
      return current as HTMLElement;
    }
    current = current.parentNode;
  }
  return null;
}

/**
 * Finds the table placeholder that would be affected by the next Backspace or Delete.
 *
 * "Adjacent" means: the placeholder is the immediate previous sibling (Backspace)
 * or next sibling (Delete) of the current cursor position. For example, with
 * "hello [cursor] [TABLE_PLACEHOLDER] world", pressing Delete at the cursor
 * would normally delete one character; we want to delete the whole placeholder
 * in one shot instead.
 *
 * Purpose: Table placeholders are treated as single units. When the user presses
 * Backspace or Delete and the cursor is right before or after a placeholder,
 * we use this to find that placeholder so the editor can delete it atomically
 * (one keypress removes the entire table) rather than doing character-by-character
 * deletion or doing nothing.
 *
 * @param node - Unused; we use window.getSelection() to know cursor position and direction
 * @param direction - 'Backspace' = placeholder immediately before cursor; 'Delete' = immediately after
 * @returns The adjacent placeholder to delete, or null if there is none
 */
export function findAdjacentTable(
  node: Node | null,
  direction: 'Backspace' | 'Delete'
): HTMLElement | null {
  if (!node) return null;

  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return null;

  const range = selection.getRangeAt(0);
  const container = range.commonAncestorContainer;

  // Cursor in a text node: adjacent placeholder is the previous/next sibling of that text node
  if (container.nodeType === Node.TEXT_NODE) {
    const textNode = container as Text;
    const offset = range.startOffset;

    if (direction === 'Backspace' && offset === 0) {
      const prevSibling = textNode.previousSibling;
      if (prevSibling && prevSibling.nodeType === Node.ELEMENT_NODE) {
        const element = prevSibling as HTMLElement;
        if (isTablePlaceholder(element)) {
          return element;
        }
      }
    } else if (direction === 'Delete' && offset === textNode.length) {
      const nextSibling = textNode.nextSibling;
      if (nextSibling && nextSibling.nodeType === Node.ELEMENT_NODE) {
        const element = nextSibling as HTMLElement;
        if (isTablePlaceholder(element)) {
          return element;
        }
      }
    }
  }

  // Cursor in an element: adjacent placeholder is the previous/next child at the cursor index
  if (container.nodeType === Node.ELEMENT_NODE) {
    const element = container as HTMLElement;

    if (isTablePlaceholder(element)) {
      return element;
    }

    const childNodes = Array.from(element.childNodes);
    const cursorPos = range.startOffset;

    if (direction === 'Backspace' && cursorPos > 0) {
      const prevNode = childNodes[cursorPos - 1];
      if (prevNode && prevNode.nodeType === Node.ELEMENT_NODE) {
        const prevElement = prevNode as HTMLElement;
        if (isTablePlaceholder(prevElement)) {
          return prevElement;
        }
      }
    } else if (direction === 'Delete' && cursorPos < childNodes.length) {
      const nextNode = childNodes[cursorPos];
      if (nextNode && nextNode.nodeType === Node.ELEMENT_NODE) {
        const nextElement = nextNode as HTMLElement;
        if (isTablePlaceholder(nextElement)) {
          return nextElement;
        }
      }
    }
  }

  return null;
}

/**
 * Replaces every table in the container with a small inline placeholder icon.
 *
 * Tables are not rendered in the editor; we show a single icon (with the original
 * HTML stored in data-original-html) so the user sees "there is a table here"
 * without the layout/editing complexity. This runs when content is loaded or
 * when the value prop updates.
 *
 * @param container - The editable div (or subtree) that contains the HTML to display
 */
export function replaceTablesWithPlaceholders(container: HTMLElement): void {
  // Match both bare <table> and <figure class="table"><table>...</table></figure>
  const tables = container.querySelectorAll('table, figure.table');

  tables.forEach((table) => {
    const tableElement = table as HTMLElement;

    const originalHtml = tableElement.outerHTML;

    const placeholder = document.createElement('span');
    placeholder.setAttribute('data-table-placeholder', 'true');
    placeholder.setAttribute('data-original-html', originalHtml);
    placeholder.setAttribute('contenteditable', 'false');
    placeholder.style.display = 'inline-block';
    placeholder.style.cursor = 'pointer';
    placeholder.style.color = EDITOR_COLORS.placeholder;
    placeholder.style.verticalAlign = 'middle';
    placeholder.style.lineHeight = '0';

    placeholder.innerHTML = `
      <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
        <path d="M5.54688 6.71875H34.4531C34.4531 6.71875 37.5781 6.71875 37.5781 9.84375V30.1562C37.5781 30.1562 37.5781 33.2812 34.4531 33.2812H5.54688C5.54688 33.2812 2.42188 33.2812 2.42188 30.1562V9.84375C2.42188 9.84375 2.42188 6.71875 5.54688 6.71875Z" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M2.42188 14.5312H37.5781" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M2.42188 23.9062H37.5781" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M14.1406 14.5312V33.2812" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M21.9531 14.5312V33.2812" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M29.7656 14.5312V33.2812" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;

    tableElement.parentNode?.replaceChild(placeholder, tableElement);
  });
}

/**
 * Converts HTML that contains table placeholders back into HTML with real tables.
 *
 * The editor stores and edits HTML that has placeholders. When we send content
 * out (e.g. on blur or when the user clicks "Replace to Chart"), we need to
 * put the actual table markup back so the recipient gets full table structure,
 * not placeholder spans. This function finds each placeholder and swaps it for
 * the value in data-original-html.
 *
 * @param html - HTML string that may contain [data-table-placeholder] elements
 * @returns HTML string with placeholders replaced by their original table markup
 */
export function restoreTablesFromPlaceholders(html: string): string {
  const temp = document.createElement('div');
  temp.innerHTML = html;

  const placeholders = temp.querySelectorAll('[data-table-placeholder]');
  placeholders.forEach((placeholder) => {
    const originalHtml = placeholder.getAttribute('data-original-html');
    if (originalHtml) {
      const tempContainer = document.createElement('div');
      tempContainer.innerHTML = originalHtml;
      const originalTable = tempContainer.firstChild;
      if (originalTable) {
        placeholder.parentNode?.replaceChild(originalTable, placeholder);
      }
    }
  });

  return temp.innerHTML;
}
